import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Copy, Eye, EyeOff, Check, AlertTriangle, RefreshCw } from 'lucide-react';
import { useMerchantAuth } from '../../contexts/MerchantAuthContext';
import MerchantLayout from '../../components/MerchantLayout';
import { toast } from 'sonner';

export default function MerchantSettingsPage() {
  const { merchant, fetchWithAuth } = useMerchantAuth();
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    callback_url: '',
    return_url: ''
  });
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [apiKey, setApiKey] = useState('***-API-KEY-HIDDEN-***');
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState({
    profile: false,
    password: false,
    apiKey: false
  });

  useEffect(() => {
    if (merchant) {
      setProfile({
        name: merchant.name || '',
        email: merchant.email || '',
        callback_url: merchant.callback_url || '',
        return_url: merchant.return_url || ''
      });
    }
  }, [merchant]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading({ ...loading, profile: true });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetchWithAuth(`${backendUrl}/api/merchant/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: profile.name,
          callback_url: profile.callback_url || null,
          return_url: profile.return_url || null
        })
      });

      if (response.ok) {
        toast.success('Perfil actualizado');
        // Update localStorage
        const updatedMerchant = { ...merchant, ...profile };
        localStorage.setItem('merchant_data', JSON.stringify(updatedMerchant));
      } else {
        throw new Error('Error updating profile');
      }
    } catch (error) {
      toast.error('Error al actualizar perfil');
    } finally {
      setLoading({ ...loading, profile: false });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (passwords.new !== passwords.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (passwords.new.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading({ ...loading, password: true });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetchWithAuth(`${backendUrl}/api/merchant/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.new
        })
      });

      if (response.ok) {
        toast.success('Contraseña actualizada');
        setPasswords({ current: '', new: '', confirm: '' });
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al cambiar contraseña');
      }
    } catch (error) {
      toast.error('Error al cambiar contraseña');
    } finally {
      setLoading({ ...loading, password: false });
    }
  };

  const handleRegenerateApiKey = async () => {
    if (!confirm('¿Estás seguro? La API Key actual dejará de funcionar.')) {
      return;
    }

    setLoading({ ...loading, apiKey: true });

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetchWithAuth(`${backendUrl}/api/merchant/regenerate-api-key`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setApiKey(data.api_key);
        setShowApiKey(true);
        toast.success('API Key regenerada. ¡Guárdala ahora!');
      } else {
        throw new Error('Error regenerating API key');
      }
    } catch (error) {
      toast.error('Error al regenerar API Key');
    } finally {
      setLoading({ ...loading, apiKey: false });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <MerchantLayout>
      <div className="space-y-6" data-testid="merchant-settings-page">
        <div>
          <h1 className="text-3xl font-bold text-[hsl(var(--brand-ink))]" style={{ fontFamily: 'var(--font-heading)' }}>
            Configuración
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">
            Gestiona tu cuenta y API
          </p>
        </div>

        {/* Profile Settings */}
        <Card className="p-6">
          <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-4">Información del Negocio</h3>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre del Negocio</Label>
              <Input
                id="name"
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="mt-2"
                data-testid="merchant-settings-name"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={profile.email}
                disabled
                className="mt-2 bg-[hsl(var(--accent))]"
                data-testid="merchant-settings-email"
              />
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">
                El email no se puede cambiar
              </p>
            </div>
            <div>
              <Label htmlFor="callback_url">Webhook URL</Label>
              <Input
                id="callback_url"
                type="url"
                placeholder="https://tu-sitio.com/webhook"
                value={profile.callback_url}
                onChange={(e) => setProfile({ ...profile, callback_url: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label htmlFor="return_url">Return URL</Label>
              <Input
                id="return_url"
                type="url"
                placeholder="https://tu-sitio.com/gracias"
                value={profile.return_url}
                onChange={(e) => setProfile({ ...profile, return_url: e.target.value })}
                className="mt-2"
              />
            </div>
            <Button type="submit" disabled={loading.profile} data-testid="merchant-update-profile-btn">
              {loading.profile ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </form>
        </Card>

        {/* API Key Section */}
        <Card className="p-6">
          <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-4">API Key</h3>
          <div className="space-y-4">
            <div>
              <Label>Tu API Key</Label>
              <div className="flex gap-2 mt-2">
                <div className="flex-1 relative">
                  <Input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    readOnly
                    className="pr-10"
                    data-testid="merchant-api-key-display"
                  />
                  <button
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--brand-teal))]"
                    data-testid="merchant-toggle-api-key-btn"
                  >
                    {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  onClick={() => copyToClipboard(apiKey)}
                  variant="outline"
                  disabled={apiKey === '***-API-KEY-HIDDEN-***'}
                  data-testid="merchant-copy-api-key-btn"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              {apiKey === '***-API-KEY-HIDDEN-***' && (
                <p className="text-xs text-yellow-600 mt-2">
                  ⚠️ La API Key solo se muestra durante el registro. Si la perdiste, regenera una nueva.
                </p>
              )}
            </div>
            <Button
              onClick={handleRegenerateApiKey}
              variant="destructive"
              disabled={loading.apiKey}
              data-testid="merchant-regenerate-api-key-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {loading.apiKey ? 'Regenerando...' : 'Regenerar API Key'}
            </Button>
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">Importante:</p>
                  <p>Regenerar tu API Key invalidará la anterior. Actualiza todas tus integraciones con la nueva key.</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Change Password */}
        <Card className="p-6">
          <h3 className="font-semibold text-[hsl(var(--brand-ink))] mb-4">Cambiar Contraseña</h3>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="current_password">Contraseña Actual</Label>
              <Input
                id="current_password"
                type="password"
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="mt-2"
                data-testid="merchant-current-password"
              />
            </div>
            <div>
              <Label htmlFor="new_password">Nueva Contraseña</Label>
              <Input
                id="new_password"
                type="password"
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="mt-2"
                data-testid="merchant-new-password"
              />
            </div>
            <div>
              <Label htmlFor="confirm_password">Confirmar Nueva Contraseña</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="mt-2"
                data-testid="merchant-confirm-new-password"
              />
            </div>
            <Button type="submit" disabled={loading.password || !passwords.current || !passwords.new} data-testid="merchant-change-password-btn">
              {loading.password ? 'Cambiando...' : 'Cambiar Contraseña'}
            </Button>
          </form>
        </Card>
      </div>
    </MerchantLayout>
  );
}
